import { memo, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type Theme } from '@mui/material/styles';

import { IconEyeOff as VisibilityOff } from '@/components/icon/tabler/IconEyeOff';
import { IconEye as Visibility } from '@/components/icon/tabler/IconEye';
import Button from '@/components/Button';
import AuthAccessCard from '@/domain/auth/components/AuthAccessCard';
import AuthFieldError from '@/domain/auth/components/AuthFieldError';
import AuthForm from '@/domain/auth/components/AuthForm';
import ForgotPassword from '@/domain/auth/components/ForgotPassword';
import { type ModalRef } from '@/components/Modal';
import { useSignin } from '@/api/hooks';
import { getInviteEmail, getPostSignInRoute } from '@/domain/invite/utils';
import { signinSchema } from '@/domain/auth/schemas';
import { type SigninRequest } from '@/api/routes/public/auth.api';
import { getUserMessage } from '@/utils/error';
import { useTranslate } from '@/state/i18n';
import { controlTokens } from '@/theme/themePrimitives';

const SignInCard = () => {
  const t = useTranslate('domain.auth.components.SignInCard');
  const forgotPasswordRef = useRef<ModalRef>(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { mutateAsync: signin, isPending, error } = useSignin();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SigninRequest>({
    resolver: joiResolver(signinSchema),
    // on blur used to auth related cards
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      // Coming from a partner invite, the invited e-mail is already filled in.
      email: getInviteEmail(searchParams),
      password: '',
    },
  });

  const handleClickOpen = () => {
    forgotPasswordRef.current?.open();
  };

  // Back to a pending partner invite; otherwise home decides (onboarding or the account gate).
  const onSubmit = async (data: SigninRequest) => {
    const response = await signin(data);
    if (response.data?.user) navigate(getPostSignInRoute(searchParams), { replace: true });
  };

  return (
    <AuthAccessCard active="signin">
      <AuthForm onSubmit={handleSubmit(onSubmit)}>
        <Box sx={sx.head}>
          <Typography component="h2" variant="h2" sx={sx.title}>
            {t('title')}
          </Typography>
          <Typography variant="body2" sx={sx.subtitle}>
            {t('subtitle')}
          </Typography>
        </Box>
        <FormControl>
          <FormLabel htmlFor="email">{t('email')}</FormLabel>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                error={!!errors.email}
                helperText={errors.email && <AuthFieldError message={errors.email.message} />}
                id="email"
                type="email"
                placeholder={t('emailPlaceholder')}
                autoComplete="email"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={errors.email ? 'error' : 'primary'}
              />
            )}
          />
        </FormControl>
        <FormControl>
          <Box sx={sx.labelRow}>
            <FormLabel htmlFor="password">{t('password')}</FormLabel>
            <Link
              component="button"
              type="button"
              onClick={handleClickOpen}
              sx={sx.forgotLink}
              data-link-tone="dark"
            >
              {t('forgotPassword')}
            </Link>
          </Box>
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                error={!!errors.password}
                helperText={errors.password && <AuthFieldError message={errors.password.message} />}
                placeholder="••••••"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                required
                fullWidth
                variant="outlined"
                color={errors.password ? 'error' : 'primary'}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
        </FormControl>
        <Button type="submit" fullWidth variant="contained" size="large" loading={isPending}>
          {t('signIn')}
        </Button>
        {error && (
          <Alert severity="error">
            <AlertTitle>{getUserMessage(error, t('signInError'))}</AlertTitle>
          </Alert>
        )}
      </AuthForm>
      <ForgotPassword ref={forgotPasswordRef} />
    </AuthAccessCard>
  );
};

export default memo(SignInCard);

const sx = {
  head: { display: 'flex', flexDirection: 'column', gap: '6px', mb: 0.5 },
  title: (theme: Theme) => ({
    fontSize: { xs: 21, sm: 24 },
    lineHeight: 1.25,
    letterSpacing: '-0.012em',
    color: controlTokens.primary,
    ...theme.applyStyles('dark', { color: 'text.primary' }),
  }),
  subtitle: { color: 'text.secondary', fontSize: { xs: 13, sm: 14 } },
  labelRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 1,
  },
  forgotLink: {
    typography: 'subtitle2',
    fontWeight: 600,
    color: controlTokens.cobalt,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline', color: controlTokens.primary },
  },
};
